import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
// import { CreateWorkspaceDirectModal } from '@/components/CreateWorkspaceDirectModal';
import { Loader2, Building } from 'lucide-react';
import { saveWorkspaceSelection } from '@/lib/workspaceStorage';

export const SelectWorkspace = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workspaces, isLoading } = useWorkspaces();
  // const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    // Always clear any stale workspace selection on entering the selector
    // to avoid accidental dashboard mounts using previous session values.
  try { sessionStorage.removeItem('currentWorkspaceId'); } catch (_) {}
  try { sessionStorage.removeItem('workspaceData'); } catch (_) {}

    // Consider session valid if we have either an in-memory token or persisted userData,
    // even if AuthProvider.user hasn't been set yet (post-OTP flow without /auth/me).
    const hasSession = (() => {
      try {
        const store = require('@/services/authStore').getAuthStore();
        if (store?.authToken) return true;
      } catch (_) {}
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) return true;
      } catch (_) {}
      return !!user;
    })();

    if (!hasSession) {
      navigate('/auth');
      return;
    }

    // Auto-redirect if user has only one workspace
    if (!isLoading && workspaces.length === 1) {
      const ws = workspaces[0];
      const wsId = String(ws.id);
      try {
        const workspaceData = {
          workspaceId: wsId,
          workspaceName: ws.name,
          userId: (ws.userId ?? (ws as any)?.raw?.userId ?? null) ? String(ws.userId ?? (ws as any)?.raw?.userId) : null,
          role: ws.role ?? (ws as any)?.raw?.role ?? null,
          authUserId: (ws.authUserId ?? (ws as any)?.raw?.authUserId ?? null) ? String(ws.authUserId ?? (ws as any)?.raw?.authUserId) : null,
        };
        saveWorkspaceSelection(workspaceData);
        try { require('@/services/authStore').setAuthStore({ workspaceData }); } catch (e) {}

        // Persist modules and subscription for sidebar and UI
        const raw = (ws as any)?.raw || {};
        const modules = raw.modules || [];
        const subscription = raw.subscription || null;
        try { localStorage.setItem(`modules:${wsId}`, JSON.stringify(modules)); } catch (e) {}
        try { localStorage.setItem(`subscription:${wsId}`, JSON.stringify(subscription)); } catch (e) {}
      } catch (_) {}
  navigate(`/dashboard`);
    }
  }, [user, workspaces, isLoading, navigate]);

  // Removed fallback direct API fetch — rely on useWorkspaces hook.

  const handleWorkspaceSelect = (workspaceId: string) => {
    const ws = workspaces.find(w => String(w.id) === String(workspaceId));
    const wsId = String(workspaceId);
    try {
      const workspaceData = {
        workspaceId: wsId,
        workspaceName: ws?.name,
        userId: (ws?.userId ?? (ws as any)?.raw?.userId ?? null) ? String(ws?.userId ?? (ws as any)?.raw?.userId) : null,
        role: ws?.role ?? (ws as any)?.raw?.role ?? null,
        authUserId: (ws?.authUserId ?? (ws as any)?.raw?.authUserId ?? null) ? String(ws?.authUserId ?? (ws as any)?.raw?.authUserId) : null,
      };
      saveWorkspaceSelection(workspaceData);
      try { require('@/services/authStore').setAuthStore({ workspaceData }); } catch (e) {}

      // Persist modules and subscription for selected workspace
      const raw = (ws as any)?.raw || {};
      const modules = raw.modules || [];
      const subscription = raw.subscription || null;
      try { localStorage.setItem(`modules:${wsId}`, JSON.stringify(modules)); } catch (e) {}
      try { localStorage.setItem(`subscription:${wsId}`, JSON.stringify(subscription)); } catch (e) {}
    } catch (_) {}
    navigate(`/dashboard`);
  };
  // const handleWorkspaceCreated = (workspaceId: string) => {
  //   setShowCreateModal(false);
  //   try { sessionStorage.setItem('currentWorkspaceId', workspaceId); } catch (e) {}
  //   try { require('@/services/authStore').setAuthStore({ workspaceData: { workspaceId } }); } catch (e) {}
  //   navigate(`/dashboard`);
  // };

  // Do not block rendering if user hasn't hydrated yet; session is already validated above.

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-lg">Loading workspaces...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Select Workspace</h1>
          <p className="text-muted-foreground mt-2">
            Choose a workspace to continue
          </p>
        </div>

        <div className="space-y-4">
          {workspaces.map((workspace) => (
            <Card key={workspace.id} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <Button
                  variant="ghost"
                  className="w-full h-auto p-0 justify-start"
                  onClick={() => handleWorkspaceSelect(workspace.id)}
                >
                  <div className="flex items-center space-x-3">
                    <Building className="h-8 w-8 text-primary" />
                    <div className="text-left">
                      <h3 className="font-semibold">{workspace.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {workspace.organization?.name || 'Organization'}
                      </p>
                    </div>
                  </div>
                </Button>
              </CardContent>
            </Card>
          ))}

          {/* Create workspace option disabled for now */}
          {false && (
            <Card className="border-dashed">
              <CardContent className="p-4">
                <Button
                  variant="ghost"
                  className="w-full h-auto p-0 justify-center"
                  // onClick={() => setShowCreateModal(true)}
                >
                  <div className="flex items-center space-x-3 text-center">
                    {/* <Plus className="h-8 w-8 text-muted-foreground" /> */}
                    <div>
                      <h3 className="font-semibold">Create New Workspace</h3>
                      <p className="text-sm text-muted-foreground">
                        Start fresh with a new workspace
                      </p>
                    </div>
                  </div>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* <CreateWorkspaceDirectModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleWorkspaceCreated}
        /> */}       
      </div>
    </div>
  );
};